import { PaginatedResource } from '../../typings/Paginated';
import { Log } from './audit-logs';
import { BaseService, ClientError, ExtendableError, GenericPaginatedParameters, InvalidValue, NotFound, PartialBy, Unauthorized, UnexpectedError } from './base-service';

export interface GetResourcePaginatedByActingBodyParameters extends GenericPaginatedParameters {
    actingBodyIds?: number[];
    status?: string;
    beginIn?: number;
    endIn?: number;
}


export class AlreadySignedReport extends ExtendableError {
    constructor(code: string) {
        super(code);
    }
}

export class CannotSignArmoryShift extends ExtendableError {
    constructor(code: string) {
        super(code);
    }
}

export class CannotEditFinishedArmoryShift extends ExtendableError {
    constructor(code: string) {
        super(code);
    }
}

export class AlreadyActiveArmourerOnUnit extends ExtendableError {
    constructor(code: string) {
        super(code);
    }
}


export class CannotOverlapArmoryShiftDate extends ExtendableError {
    estimatedStart: Date;
    estimatedFinish: Date;
    constructor(code: string, estimatedStart: Date, estimatedFinish: Date) {
        super(code);
        this.estimatedStart = estimatedStart;
        this.estimatedFinish = estimatedFinish;
    }
}

export class ArmoryService extends BaseService {

    async getArmoryShifts(params: GetResourcePaginatedByActingBodyParameters): Promise<PaginatedResource<ArmoryShift>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/armory-shifts${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getArmoryShift(id: number): Promise<ArmoryShift> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/armory-shift/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async deleteArmoryShift(id: number, reason: string) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/armory-shift/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({ reason })
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async createArmoryShift(armory: Omit<BaseArmoryShift, 'id'> & { assistantArmourersIds: number[]; }): Promise<ArmoryShift> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/armory-shift`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(armory)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'CannotOverlapArmoryShiftDate') {
                throw new CannotOverlapArmoryShiftDate(resJson.code, resJson.estimatedStart, resJson.estimatedFinish);
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updateArmoryShift(armory: PartialBy<Omit<BaseArmoryShift, 'actingBodyId' | 'actingBodyUnitId'>, 'id'> & { assistantArmourersIds: number[]; }): Promise<ArmoryShift> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/armory-shift/${armory.id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(armory)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'CannotOverlapArmoryShiftDate') {
                throw new CannotOverlapArmoryShiftDate(resJson.code, resJson.estimatedStart, resJson.estimatedFinish);
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getArmourers({ actingBodyId }: {
        actingBodyId?: number;
    }): Promise<SimplifiedUser[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/armory-shift/armourers${this.encodeQueryParams({ actingBodyId })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getAssistantArmourers({ armoryShiftId }: {
        armoryShiftId: number;
    }): Promise<SimplifiedUser[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/armory-shift/${armoryShiftId}/assistant-armourers`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getArmourerShifts(): Promise<{ id: number; estimatedStart: Date; estimatedFinish: Date; }[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/armory-shift/armourer-shifts`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getArmourerActiveShift(): Promise<ArmoryShift> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/armory-shift/armourer-active-armory`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 404) {
            throw new NotFound();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getArmourerPreviousShift(): Promise<ArmoryShift | null> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/armory-shift/armourer-previous-armory-shift`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async setArmourerActiveShift(armoryShiftId: number) {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/armory-shift/set-armourer-shift`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({ armoryShiftId })
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 404) {
            throw new NotFound();
        }

        if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'InvalidValue') {
                throw new InvalidValue(resJson.field);
            } else if (resJson.code == 'AlreadyActiveArmourerOnUnit') {
                throw new AlreadyActiveArmourerOnUnit(resJson.code);
            }

            throw new ClientError(resJson.code);
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async replaceInChargeArmourer(params: { armoryShiftId: number; newInChargeArmourerId: number; }) {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/armory-shift/${params.armoryShiftId}/replace-in-charge-armourer`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({
                newInChargeArmourerId: params.newInChargeArmourerId
            })
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 404) {
            throw new NotFound();
        }

        if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'InvalidValue') {
                throw new InvalidValue(resJson.field);
            }
            throw new ClientError(resJson.code);
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }


    async saveDependenciesAndDocuments(params: {
        armoryShiftId: number;
        documents: string | null;
        dependencies: string | null;
    }) {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/armory-shift/save-report/${params.armoryShiftId}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({
                documents: params.documents,
                dependencies: params.dependencies
            })
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 404) {
            throw new NotFound();
        }

        if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'InvalidValue') {
                throw new InvalidValue(resJson.field);
            } else if (resJson.code == 'AlreadyActiveArmourerOnUnit') {
                throw new AlreadyActiveArmourerOnUnit(resJson.code);
            }

            throw new ClientError(resJson.code);
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async signHandoverReport(armoryShiftId: number, params: { password?: string; rejectionReason?: string; }) {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/armory-shift/${armoryShiftId}/sign-handover-report`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status == 404) {
            throw new NotFound();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'AlreadySignedReport') {
                throw new AlreadySignedReport(resJson.code);
            } else if (resJson.code == 'CannotSignArmoryShift') {
                throw new CannotSignArmoryShift(resJson.code);
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async finishArmoryShift(armoryShiftId: number, params: { password: string; }) {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/armory-shift/${armoryShiftId}/finish`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status == 404) {
            throw new NotFound();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'CannotEditFinishedArmoryShift') {
                throw new CannotEditFinishedArmoryShift(resJson.code);
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async getArmoryShiftHistoric(id: number): Promise<Log[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/armory-shift/${id}/historic`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getHandoverReportUrl(armoryShiftId: number): Promise<{ id: number; resultUrl: string; }> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/armory-shift/handover-report-url/${armoryShiftId}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status == 404) {
            throw new NotFound();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }
}

export const armoryService = new ArmoryService();
